import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { ROLE_LABELS, type Role } from '@pizza-roma/shared';
import { ApiError } from '../../lib/api';
import { homeFor, useAuth } from '../../lib/auth';
import { LangToggle, useI18n } from '../../i18n';

/** Demo accounts. The key is used by the direct links `/login?demo=<key>` (sign-in without typing). */
const DEMO: readonly (readonly [string, Role, string])[] = [
  ['owner@fornavia.example', 'OWNER', 'proprietaire'],
  ['manager@fornavia.example', 'MANAGER', 'gerant'],
  ['kitchen@fornavia.example', 'KITCHEN', 'cuisine'],
  ['driver@fornavia.example', 'DRIVER', 'livreur'],
  ['customer@fornavia.example', 'CUSTOMER', 'client'],
  ['admin@plateforme.example', 'PLATFORM_ADMIN', 'admin'],
];
const DEMO_PASSWORD = 'Demo123!';

export function LoginPage() {
  const { login, me, loading } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = params.get('next');
  const demoEmail = DEMO.find(([, , key]) => key === params.get('demo'))?.[0];
  const demoStarted = useRef(false);
  const signInError = t.login.signInError;

  useEffect(() => {
    if (demoEmail) return;
    if (me?.user && !params.get('switch')) nav(next ?? homeFor(me), { replace: true });
  }, [demoEmail, me, nav, next, params]);

  // Direct demo link: signs in to the requested account, then opens its portal.
  useEffect(() => {
    if (!demoEmail || loading || demoStarted.current) return;
    demoStarted.current = true;
    if (me?.user?.email === demoEmail) {
      nav(next ?? homeFor(me), { replace: true });
      return;
    }
    setBusy(true);
    login(demoEmail, DEMO_PASSWORD)
      .then((m) => nav(next ?? homeFor(m), { replace: true }))
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : signInError);
        setBusy(false);
      });
  }, [demoEmail, loading, login, me, nav, next, signInError]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const m = await login(email, password);
      nav(next ?? homeFor(m), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.login.signInError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-side">
        <img className="logo" src="/brand/logo.png" alt="Pizza Fornavia" />
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 44 }}>{t.login.platformTitle}</h1>
          <p style={{ color: '#ccc', marginTop: 12, maxWidth: 420 }}>{t.login.platformPitch}</p>
        </div>
        <p className="small" style={{ color: '#888' }}>
          {t.login.platformFooter}
        </p>
      </div>
      <div className="auth-form">
        <form className="card stack-lg" onSubmit={submit}>
          <div className="row-between" style={{ alignItems: 'start' }}>
            <div>
              <h2>{t.login.title}</h2>
              <p className="muted small">{t.login.subtitle}</p>
            </div>
            <LangToggle />
          </div>
          <label className="field">
            <span>{t.common.email}</span>
            <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" data-testid="login-email" />
          </label>
          <label className="field">
            <span>{t.common.password}</span>
            <input className="input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password" />
          </label>
          {error && <div className="alert alert-error">{error}</div>}
          <button className="btn btn-primary btn-lg" disabled={busy} data-testid="login-submit">
            <LogIn size={18} /> {t.login.submit}
          </button>
          <div className="stack" style={{ gap: 6 }}>
            <p className="small muted">{t.login.demoAccounts}</p>
            <div className="demo-accounts">
              {DEMO.map(([e, role]) => (
                <button
                  type="button"
                  key={e}
                  onClick={() => {
                    setEmail(e);
                    setPassword(DEMO_PASSWORD);
                  }}
                >
                  <span>{e}</span>
                  <b>{ROLE_LABELS[lang][role]}</b>
                </button>
              ))}
            </div>
          </div>
          <Link to="/r/pizzeria-fornavia" className="small center">
            {t.login.backToSite}
          </Link>
        </form>
      </div>
    </div>
  );
}
